import { useEffect } from 'react'

import { F0Select, F0SelectItemObject } from '@factorialco/f0-react'
import { useDataCollectionData } from '@factorialco/f0-react/dist/experimental'
import {
  BankAccountSelectorF0Document,
  BankAccountSelectorF0Query,
  BankingEntitiesBankAccountNumberFormatEnum,
  BankingProvidersServicesEnumEnum,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { formattedBankAccountNumber } from 'modules/banking/utils'

type BankAccountOption = NonNullable<
  BankAccountSelectorF0Query['banking']
>['bankAccountsConnection']['edges'][number]['node']

export interface BankAccountSelectorF0Props {
  value?: BankAccountOption | null
  onChange: (bankAccount: BankAccountOption | null) => void
  label?: string
  placeholder?: string
  error?: string
  providerService?: BankingProvidersServicesEnumEnum
  accountNumberTypes?: BankingEntitiesBankAccountNumberFormatEnum[]
  currency?: string
  required?: boolean
  filter?: (account: BankAccountOption) => boolean
  autoSelectSingle?: boolean
}

const getAccountLabel = (bankAccount: BankAccountOption): string => {
  const formattedNumber = formattedBankAccountNumber(bankAccount)
  return bankAccount.accountAlias
    ? `${bankAccount.accountAlias} - **** ${formattedNumber.slice(-4)}`
    : formattedNumber
}

export const BankAccountSelectorF0 = ({
  value,
  onChange,
  label = i18n.t('banking.bulk_payment.forms.fields.account.label'),
  placeholder = i18n.t('banking.bulk_payment.forms.fields.account.placeholder'),
  error,
  providerService,
  accountNumberTypes,
  currency,
  required,
  filter,
  autoSelectSingle = false,
}: BankAccountSelectorF0Props) => {
  const source = useGraphqlDataCollectionSource(
    {
      document: BankAccountSelectorF0Document,
      paginationType: 'infinite-scroll',
      perPage: 20,
      mapVariables: ({ search }) => ({
        search: search || undefined,
        providerService,
        accountNumberTypes,
        currency,
      }),
      mapData: (data) => {
        const accounts = data.banking?.bankAccountsConnection?.edges?.map((edge) => edge.node) ?? []
        return filter ? accounts.filter(filter) : accounts
      },
      mapPagination: (data) => ({
        total: data.banking?.bankAccountsConnection?.totalCount ?? 0,
        cursor: data.banking?.bankAccountsConnection?.pageInfo?.endCursor ?? undefined,
        hasNextPage: data.banking?.bankAccountsConnection?.pageInfo?.hasNextPage ?? false,
      }),
    },
    [providerService, accountNumberTypes, currency, filter]
  )

  const { data } = useDataCollectionData(source)
  const selectedId = value?.id

  useEffect(() => {
    const records = data?.records ?? []
    if (!autoSelectSingle || records.length !== 1) return
    const singleRecord = records[0]
    if (singleRecord && selectedId !== singleRecord.id) {
      onChange(singleRecord)
    }
  }, [autoSelectSingle, data?.records, selectedId, onChange])

  const mapOptions = (
    account: BankAccountOption
  ): F0SelectItemObject<string, BankAccountOption> => ({
    value: String(account.id),
    label: getAccountLabel(account),
    description: account.beneficiaryName ?? undefined,
    item: account,
  })

  return (
    <F0Select<string, BankAccountOption>
      label={label}
      placeholder={placeholder}
      source={source}
      mapOptions={mapOptions}
      value={value ? String(value.id) : undefined}
      onChange={(selectedValue: string, originalItem?: BankAccountOption) => {
        onChange(originalItem ?? null)
      }}
      error={error}
      required={required}
      clearable
    />
  )
}
